import axios from "axios"
import accessToken from "./jwt-token-access/accessToken"
import { okara_api } from "../utils/variables"

//pass new generated access token here
const token = accessToken

//apply base url for axios
const API_URL = ""

const axiosApi = axios.create({
  baseURL: API_URL,
})

// Ensure each request sends the latest token from localStorage
axiosApi.interceptors.request.use(
  config => {
    try {
      const stored = localStorage.getItem('authToken')
      if (stored) {
        // stored may already include 'Bearer ' prefix in parts of this codebase;
        // if not, many parts expect `Bearer <token>` so keep it as-is.
        config.headers = config.headers || {}
        config.headers['Authorization'] = stored.startsWith('Bearer') ? stored : `Bearer ${stored}`
      }
    } catch (_) {}
    return config
  },
  error => Promise.reject(error)
)

axiosApi.interceptors.response.use(
  response => response,
  error => {
    try {
      const status = error?.response?.status
      const responseData = error?.response?.data
      
      if (status === 401 || status === 403) {
        // Debug logging
        console.log('API Error Details:', {
          status,
          responseData,
          url: error.config?.url,
          method: error.config?.method
        })
        
        // Check if this is a tier-based access error - more comprehensive detection
        const isTierError = 
          responseData?.error?.toLowerCase().includes('tier') || 
          responseData?.message?.toLowerCase().includes('tier') ||
          responseData?.error?.toLowerCase().includes('upgrade') ||
          responseData?.message?.toLowerCase().includes('upgrade') ||
          responseData?.error?.toLowerCase().includes('subscription') ||
          responseData?.message?.toLowerCase().includes('subscription') ||
          responseData?.error?.toLowerCase().includes('plan') ||
          responseData?.message?.toLowerCase().includes('plan') ||
          responseData?.error?.toLowerCase().includes('access') ||
          responseData?.message?.toLowerCase().includes('access') ||
          // Check if it's the AI CMO endpoint specifically
          error.config?.url?.includes(okara_api) ||
          error.config?.url?.includes('/chat/') ||
          error.config?.url?.includes('/generate-article/') ||
          // Check response status text
          error.response?.statusText?.toLowerCase().includes('forbidden') ||
          // Check for any response data that indicates tier issue
          (typeof responseData === 'string' && responseData.toLowerCase().includes('tier')) ||
          (typeof responseData === 'string' && responseData.toLowerCase().includes('upgrade'))
        
        console.log('Is Tier Error:', isTierError)
        
        // Only force a redirect when there is an authenticated user in storage.
        // This avoids redirecting visitors on public pages whose requests may
        // return 401/403 for unauthenticated access.
        const hasAuthUser = (() => {
          try {
            return !!localStorage.getItem('authUser')
          } catch (_) {
            return false
          }
        })()

        const isGuest = (() => {
          try {
            const authUser = JSON.parse(localStorage.getItem('authUser'))
            return authUser?.data?.is_guest === true || authUser?.is_guest === true
          } catch (_) {
            return false
          }
        })()

        // Handle tier-based access errors differently
        // Also treat all 403 errors from AI CMO endpoints as tier errors
        const isAICMOEndpoint = error.config?.url?.includes(okara_api) ||
                               error.config?.url?.includes('/chat/') ||
                               error.config?.url?.includes('/generate-article/')
        
        if (isTierError || (status === 403 && isAICMOEndpoint)) {
          // Tag the error so UI components can render an "Upgrade Required" message
          error.isTierRestricted = true
          error.tierMessage = responseData?.message || responseData?.error || 'This feature requires a higher tier subscription to access AI CMO features.'
          console.log('Tagged as Tier Restricted Error')
        } else if (hasAuthUser && !(isGuest && (status === 403 || status === 401))) {
          // Regular auth errors - logout the user
          try { localStorage.clear() } catch (_) {}
          try { sessionStorage.clear() } catch (_) {}
          if (typeof window !== 'undefined') {
            window.location = '/login'
          }
        } else if (isGuest && (status === 403 || status === 401)) {
          // Tag the error so UI components can render a "Guest Restricted" placeholder
          error.isGuestRestricted = true
        }
      }
    } catch (_) {}
    return Promise.reject(error)
  }
)

export async function get(url, config = {}) {
  return await axiosApi.get(url, { ...config }).then(response => response.data)
}

export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, data, { ...config })
    .then(response => response.data)
}

export async function put(url, data, config = {}) {
  return axiosApi
    .put(url, data, { ...config })
    .then(response => response.data)
}

export async function del(url, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then(response => response.data)
}

// Export the configured instance so other modules can benefit from interceptors
export default axiosApi
